/**
 * Editable preview table after POST /api/import/extract — user confirms then saves.
 */
import React from 'react';

const uncertainClass = (row, field) =>
  Array.isArray(row.uncertain_fields) && row.uncertain_fields.includes(field)
    ? 'ring-1 ring-warning border-warning bg-pine-tint/40'
    : 'border-hairline';

const ImportPreviewTable = ({
  importType,
  rows,
  onChange,
  onSave,
  onCancel,
  saving,
  saveLabel = 'Save',
}) => {
  if (!rows?.length) return null;

  const isUncertain = (row, field) =>
    Array.isArray(row.uncertain_fields) && row.uncertain_fields.includes(field);

  const updateRow = (index, field, value) => {
    const next = rows.map((r, i) => {
      if (i !== index) return r;
      const updated = { ...r, [field]: value };
      if (Array.isArray(updated.uncertain_fields)) {
        updated.uncertain_fields = updated.uncertain_fields.filter((f) => f !== field);
      }
      return updated;
    });
    onChange(next);
  };

  const removeRow = (index) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  const tableShell = (title, head, body) => (
    <div className="mt-4 surface-panel p-4 light-surface">
      <p className="text-body text-primary font-medium mb-3">{title}</p>
      <div className="overflow-x-auto">
        <table className="import-table">
          <thead>
            <tr>{head}</tr>
          </thead>
          <tbody>{body}</tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <button type="button" onClick={onSave} disabled={saving || !rows.length} className="btn-primary">
          {saving ? 'Saving…' : saveLabel}
        </button>
        <button type="button" onClick={onCancel} className="btn-ghost">
          Cancel
        </button>
      </div>
    </div>
  );

  if (importType === 'accounts') {
    return tableShell(
      'Review accounts — confirm highlighted fields, then save',
      <>
        <th>Name</th>
        <th>Type</th>
        <th>Balance</th>
        <th>Institution</th>
        <th className="w-8" />
      </>,
      rows.map((row, i) => (
        <tr key={i}>
          <td>
            <input
              className={`input-editorial w-full ${uncertainClass(row, 'name')}`}
              value={row.name || ''}
              title={isUncertain(row, 'name') ? 'Please verify this value' : undefined}
              onChange={(e) => updateRow(i, 'name', e.target.value)}
            />
          </td>
          <td>
            <select
              className={`select-editorial ${uncertainClass(row, 'type')}`}
              value={row.type || 'checking'}
              onChange={(e) => updateRow(i, 'type', e.target.value)}
            >
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
              <option value="investment">Investment</option>
            </select>
          </td>
          <td>
            <input
              type="number"
              min="0"
              step="0.01"
              className={`input-editorial figure w-24 ${uncertainClass(row, 'balance')}`}
              value={row.balance ?? ''}
              title={isUncertain(row, 'balance') ? 'Please verify balance' : undefined}
              onChange={(e) => updateRow(i, 'balance', parseFloat(e.target.value) || 0)}
            />
          </td>
          <td>
            <input
              className={`input-editorial w-full ${uncertainClass(row, 'institution')}`}
              value={row.institution || ''}
              onChange={(e) => updateRow(i, 'institution', e.target.value)}
            />
          </td>
          <td>
            <button type="button" onClick={() => removeRow(i)} className="btn-ghost text-danger text-label px-2">
              ×
            </button>
          </td>
        </tr>
      ))
    );
  }

  return tableShell(
    'Review holdings — confirm highlighted fields, then save',
    <>
      <th>Ticker</th>
      <th>Name</th>
      <th className="figure">Qty</th>
      <th className="figure">Value ($)</th>
      <th>Account</th>
      <th className="w-8" />
    </>,
    rows.map((row, i) => (
      <tr key={i}>
        <td>
          <input
            className={`input-editorial figure w-16 ${uncertainClass(row, 'ticker')}`}
            value={row.ticker || ''}
            title={isUncertain(row, 'ticker') ? 'Please verify ticker' : undefined}
            onChange={(e) => updateRow(i, 'ticker', e.target.value.toUpperCase())}
          />
        </td>
        <td>
          <input
            className={`input-editorial w-full min-w-[120px] ${uncertainClass(row, 'name')}`}
            value={row.name || ''}
            onChange={(e) => updateRow(i, 'name', e.target.value)}
          />
        </td>
        <td>
          <input
            type="number"
            min="0"
            step="any"
            className={`input-editorial figure w-20 ${uncertainClass(row, 'quantity')}`}
            value={row.quantity ?? ''}
            title={isUncertain(row, 'quantity') ? 'Please verify quantity' : undefined}
            onChange={(e) =>
              updateRow(i, 'quantity', e.target.value === '' ? null : parseFloat(e.target.value))
            }
          />
        </td>
        <td>
          <input
            type="number"
            min="0"
            step="0.01"
            className={`input-editorial figure w-24 ${uncertainClass(row, 'value')}`}
            value={row.value ?? ''}
            title={isUncertain(row, 'value') ? 'Please verify value' : undefined}
            onChange={(e) =>
              updateRow(i, 'value', e.target.value === '' ? null : parseFloat(e.target.value))
            }
          />
        </td>
        <td>
          <input
            className={`input-editorial w-full ${uncertainClass(row, 'account')}`}
            value={row.account || ''}
            onChange={(e) => updateRow(i, 'account', e.target.value)}
          />
        </td>
        <td>
          <button type="button" onClick={() => removeRow(i)} className="btn-ghost text-danger text-label px-2">
            ×
          </button>
        </td>
      </tr>
    ))
  );
};

export default ImportPreviewTable;
